import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { RefreshAccessToken } from '@wilson/auth/core';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  of,
  switchMap,
  timer,
} from 'rxjs';
import {
  RealtimeClientAction,
  RealtimeServerEvent,
  parseRealtimeServerEvent,
} from './realtime.types';
import { REALTIME_WS_URL } from './realtime.config';

const MAX_RECONNECT_ATTEMPTS = 6;
const BASE_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const PING_INTERVAL_MS = 60_000;

/**
 * This service is responsible for managing the WebSocket connection to the realtime server.
 * It handles the connection lifecycle, message handling, and reconnection logic.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeSocketService implements OnDestroy {
  private readonly store = inject(Store);
  private readonly wsUrl = inject(REALTIME_WS_URL);

  private socket?: WebSocket;
  private manualDisconnect = false;
  private backgroundPaused = false;
  private reconnectAttempt = 0;
  private reconnectSubscription?: Subscription;
  private pingSubscription?: Subscription;
  private onOpenCallback?: () => void;

  private readonly incomingSubject = new Subject<RealtimeServerEvent>();

  readonly incomingActions$: Observable<RealtimeServerEvent> =
    this.incomingSubject.asObservable();

  get isConfigured(): boolean {
    return this.wsUrl.length > 0;
  }

  isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  connect(onOpen?: () => void): void {
    if (!this.isConfigured) {
      return;
    }

    this.manualDisconnect = false;

    if (this.socket?.readyState === WebSocket.OPEN) {
      onOpen?.();
      return;
    }

    if (onOpen) {
      const previousCallback = this.onOpenCallback;
      this.onOpenCallback = () => {
        previousCallback?.();
        onOpen();
      };
    }

    if (this.socket?.readyState === WebSocket.CONNECTING) {
      return;
    }

    this.openSocket();
  }

  send(action: RealtimeClientAction): void {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      return;
    }

    this.socket.send(JSON.stringify(action));
  }

  disconnect(): void {
    this.manualDisconnect = true;
    this.backgroundPaused = false;
    this.stopPing();
    this.reconnectSubscription?.unsubscribe();
    this.reconnectSubscription = undefined;
    this.onOpenCallback = undefined;
    this.socket?.close();
    this.socket = undefined;
  }

  pauseKeepalive(): void {
    this.backgroundPaused = true;
    this.stopPing();
    this.reconnectSubscription?.unsubscribe();
    this.reconnectSubscription = undefined;
  }

  resumeKeepalive(): void {
    this.backgroundPaused = false;
    if (this.isOpen()) {
      this.startPing();
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.incomingSubject.complete();
  }

  private openSocket(): void {
    const token = this.store.selectSnapshot(
      (state) => state.auth.accessToken as string | null,
    );

    if (!token) {
      return;
    }

    /**
     * Opens an authenticated WebSocket for the view-coordinator API.
     * Browsers cannot set Authorization headers on the handshake, so the token
     * is passed via Sec-WebSocket-Protocol.
     */
    this.socket = new WebSocket(this.wsUrl, [token]);
    this.socket.onopen = () => {
      this.reconnectAttempt = 0;
      if (!this.backgroundPaused) {
        this.startPing();
      }
      this.onOpenCallback?.();
      this.onOpenCallback = undefined;
    };
    this.socket.onmessage = (event) => this.handleMessage(event.data);
    this.socket.onclose = () => this.handleClose();
    this.socket.onerror = () => {
      this.socket?.close();
    };
  }

  private handleMessage(raw: unknown): void {
    if (typeof raw !== 'string') {
      return;
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      const event = parseRealtimeServerEvent(parsed);
      if (event) {
        this.incomingSubject.next(event);
      }
    } catch {
      // Ignore malformed messages.
    }
  }

  private handleClose(): void {
    this.stopPing();
    this.socket = undefined;
    this.onOpenCallback = undefined;

    if (this.manualDisconnect || this.backgroundPaused) {
      return;
    }

    if (this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      return;
    }

    const jitter = Math.random() * 500;
    const delay = Math.min(
      BASE_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempt + jitter,
      MAX_RECONNECT_DELAY_MS,
    );
    this.reconnectAttempt += 1;

    this.reconnectSubscription?.unsubscribe();
    this.reconnectSubscription = timer(delay)
      .pipe(
        switchMap(() =>
          this.store
            .dispatch(new RefreshAccessToken())
            .pipe(catchError(() => of(null))),
        ),
      )
      .subscribe(() => this.openSocket());
  }

  private startPing(): void {
    this.stopPing();
    this.pingSubscription = timer(PING_INTERVAL_MS, PING_INTERVAL_MS).subscribe(
      () => this.send({ action: 'ping' }),
    );
  }

  private stopPing(): void {
    this.pingSubscription?.unsubscribe();
    this.pingSubscription = undefined;
  }
}
